/**
 * Available score display modes as enum-like object
 */
export const SCORE_DISPLAY_MODE = {
  RAW: 'raw',
  PERCENTAGE: 'percentage',
  LEVEL: 'level',
} as const

/** Array of all display modes for iteration */
export const SCORE_DISPLAY_MODES = Object.values(SCORE_DISPLAY_MODE)

/** Default score display mode */
export const DEFAULT_SCORE_DISPLAY_MODE: ScoreDisplayMode = SCORE_DISPLAY_MODE.RAW

/**
 * Score display modes for similar issues
 * - 'raw': Show the raw Elasticsearch score (e.g., "8.5")
 * - 'percentage': Show as percentage using sigmoid formula (e.g., "86%")
 * - 'level': Show as Low/Medium/High based on thresholds
 */
export type ScoreDisplayMode = (typeof SCORE_DISPLAY_MODE)[keyof typeof SCORE_DISPLAY_MODE]

/**
 * Available score levels
 */
export const SCORE_LEVELS = ['High', 'Medium', 'Low'] as const

/** Level type for similarity scores */
export type ScoreLevel = (typeof SCORE_LEVELS)[number]

/** Primer Label variant type for score display */
export type ScoreLabelVariant = 'success' | 'attention' | 'secondary'

/** Divisor for sigmoid formula - controls the steepness of the curve */
const SIGMOID_DIVISOR = 6

/** Percentage threshold for "High" confidence level */
const HIGH_THRESHOLD = 80

/** Percentage threshold for "Medium" confidence level */
const MEDIUM_THRESHOLD = 60

/**
 * Mapping from score level to Primer Label variant
 */
const SCORE_LEVEL_VARIANTS: Record<ScoreLevel, ScoreLabelVariant> = {
  High: 'success',
  Medium: 'attention',
  Low: 'secondary',
}

/**
 * Convert raw ES score to percentage using sigmoid formula.
 * Formula: 100 × (1 - e^(-score/SIGMOID_DIVISOR))
 *
 * Score-to-Percentage mapping:
 * - 4 → 49%
 * - 6 → 63%
 * - 8 → 74%
 * - 10 → 81%
 * - 12 → 86%
 */
export function scoreToPercentage(score: number): number {
  return Math.round(100 * (1 - Math.exp(-score / SIGMOID_DIVISOR)))
}

/**
 * Convert percentage to level (Low/Medium/High)
 * - High: >= 80%
 * - Medium: >= 60%
 * - Low: < 60%
 */
export function percentageToLevel(percentage: number): ScoreLevel {
  if (percentage >= HIGH_THRESHOLD) return 'High'
  if (percentage >= MEDIUM_THRESHOLD) return 'Medium'
  return 'Low'
}

/**
 * Format a similarity score based on the display mode
 */
export function formatScore(score: number, mode: ScoreDisplayMode): string {
  switch (mode) {
    case SCORE_DISPLAY_MODE.RAW:
      return score.toFixed(1)
    case SCORE_DISPLAY_MODE.PERCENTAGE: {
      const percentage = scoreToPercentage(score)
      return `${percentage}%`
    }
    case SCORE_DISPLAY_MODE.LEVEL: {
      const percentage = scoreToPercentage(score)
      return percentageToLevel(percentage)
    }
  }
}

/**
 * Get the Label variant based on the score level
 */
export function getScoreLabelVariant(score: number): ScoreLabelVariant {
  const percentage = scoreToPercentage(score)
  const level = percentageToLevel(percentage)
  return SCORE_LEVEL_VARIANTS[level]
}

/**
 * Display mode labels for the settings menu
 */
export const SCORE_DISPLAY_MODE_LABELS: Record<ScoreDisplayMode, string> = {
  [SCORE_DISPLAY_MODE.RAW]: 'Raw score',
  [SCORE_DISPLAY_MODE.PERCENTAGE]: 'Percentage',
  [SCORE_DISPLAY_MODE.LEVEL]: 'Low/Medium/High',
}
